<template>
    <div v-if="questions.length > 0" class="FeedbackForm">
        <FeedbackQuestion
            :key="questionIndex"
            :options="question.options"
            :placeholder="
                placeholderTemplate
                    ? insertParamIfProvided($t(placeholderTemplate[0], lang), placeholderParam)
                    : undefined
            "
            :question="insertParamIfProvided($t(questionTemplate[0] || '', lang), questionParam)"
            :text-only="question.textOnly"
            @input="updateAnswer($event)"
            @rate="emit('rate', $event)"
        />
        <div class="flex justify-center">
            <CButton
                class="mt-4 w-full text-center uppercase sm:w-3/4"
                :disabled="!isComplete"
                size="large"
                variant="primary"
                @click="lastQuestion ? submitAnswers() : nextStep()"
            >
                {{
                    lastQuestion ? $t('feedbacks.modal.buttons.submit', lang) : $t('feedbacks.modal.buttons.next', lang)
                }}
            </CButton>
        </div>
    </div>
    <div v-else class="FeedbackForm">
        <p>{{ $t('feedbacks.noQuestionsAvailable', lang) }}</p>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PropType } from 'vue'

import {
    FeedbackAction,
    FeedbackAnswerData,
    FeedbackOption,
    FeedbackQuestionData,
    feedbackQuestions
} from '#shared/types/Feedback'
import type { UserName } from '#shared/types/User'
import { getFullName, pickTemplateByPrevAnswer } from '#shared/utils'
import { isNpsSatisfied } from '#shared/feedback'
import { shouldBeInformal } from '@/utils/feedback'

const props = defineProps({
    action: {
        type: String as PropType<FeedbackAction>,
        required: true
    },
    nps: {
        type: Number,
        default: undefined
    },
    subject: {
        type: Object as PropType<UserName | undefined>,
        default: undefined
    },
    lang: {
        type: String,
        default: 'sk'
    }
})

const emit = defineEmits(['submit', 'rate'])

const questionIndex = ref(0)
const answers = ref<Record<number, FeedbackAnswerData>>({})
const questions = ref<FeedbackQuestionData[]>(feedbackQuestions[props.action] as FeedbackQuestionData[] || [])

const isComplete = computed((): boolean => {
    return Boolean(
        answer.value.nps !== undefined ||
            (answer.value.option && answer.value.option !== FeedbackOption.OTHER_REASON) ||
            answer.value.message
    )
})

// The score for the current step - either the one just given, or the one carried in
// from the e-mail link for the first step.
const currentScore = computed((): number | undefined => {
    return answers.value[questionIndex.value]?.nps ?? props.nps
})

const answer = computed((): FeedbackAnswerData => {
    return answers.value[questionIndex.value] || ({} as FeedbackAnswerData)
})

const question = computed((): FeedbackQuestionData => {
    return questions.value[questionIndex.value] || {} as FeedbackQuestionData
})

const lastQuestion = computed((): boolean => {
    return questionIndex.value === (questions.value?.length || 1) - 1
})

const questionTemplate = computed((): string[] => {
    return pickTemplateByPrevAnswer(question.value?.question || '', currentScore.value)
})

const questionParam = computed((): string | null => {
    if (questionTemplate.value?.[1] && ['volunteer', 'mentor', 'mentee'].includes(questionTemplate.value[1])) {
        return getFullName(props.subject as UserName)
    }
    return null
})

const placeholderTemplate = computed((): string[] | undefined => {
    if (question.value?.placeholder) {
        return pickTemplateByPrevAnswer(question.value.placeholder, currentScore.value)
    }
    if (currentScore.value !== undefined) {
        return [
            isNpsSatisfied(currentScore.value)
                ? 'feedbacks.form.nps.followUp.satisfied'
                : 'feedbacks.form.nps.followUp.notSatisfied'
        ]
    }
    return [`feedbacks.form.headline.${formal.value ? 'formal' : 'informal'}.oneThingToChange`]
})

const placeholderParam = computed((): string | null => {
    return null
})

const formal = computed((): boolean => {
    return !shouldBeInformal(props.action)
})

function insertParamIfProvided(template: string, param?: string | null) {
    return template.replace('{{PARAM}}', param || '')
}

function nextStep() {
    if (!lastQuestion.value) {
        questionIndex.value++
    }
}

function updateAnswer(data: FeedbackAnswerData) {
    answers.value = { ...answers.value, [questionIndex.value]: data }
}

function submitAnswers() {
    emit('submit', Object.values(answers.value))
}
</script>